import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../../MainURL';
import { getAdminSession } from '../../adminSession';
import '../../Admin.scss';

type TicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed';

type Ticket = {
  id: number;
  authChurch: string;
  requesterName: string;
  requesterContact: string;
  subject: string;
  status: TicketStatus;
  priority: string;
  createdAt: string;
  updatedAt: string;
};

type Reply = {
  id: number;
  ticketId: number;
  authorType: 'admin' | 'church';
  authorName: string;
  message: string;
  createdAt: string;
};

type TicketDetail = Ticket & { message: string; replies: Reply[] };

type ApiEnvelope<T> = { ok: boolean; data?: T; message?: string };

const STATUS_LABEL: Record<TicketStatus, string> = {
  open: '접수',
  in_progress: '처리중',
  resolved: '해결됨',
  closed: '종료',
};

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${yyyy}.${mm}.${dd} ${hh}:${min}`;
}

function errorMessageOf(err: unknown, fallback: string): string {
  const data = axios.isAxiosError(err) ? err.response?.data : null;
  if (data && typeof data === 'object' && 'message' in data) {
    return String((data as { message?: string }).message) || fallback;
  }
  return fallback;
}

const EMPTY_FORM = { authChurch: '', requesterName: '', requesterContact: '', subject: '', message: '' };

export default function SupportTickets() {
  const session = getAdminSession();
  const [items, setItems] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | TicketStatus>('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<TicketDetail | null>(null);
  const [replyText, setReplyText] = useState('');

  const fetchTickets = useCallback(async (status: '' | TicketStatus) => {
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await axios.get<ApiEnvelope<Ticket[]>>(`${MainURL}/admin-console/support-tickets`, {
        params: status ? { status } : {},
      });
      if (res.data?.ok && res.data.data) {
        setItems(res.data.data);
      } else {
        setErrorMsg(res.data?.message || '티켓 목록을 불러오지 못했습니다.');
      }
    } catch (err) {
      setErrorMsg(errorMessageOf(err, '티켓 목록을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets(statusFilter);
  }, [fetchTickets, statusFilter]);

  const openDetail = async (id: number) => {
    try {
      const res = await axios.get<ApiEnvelope<TicketDetail>>(`${MainURL}/admin-console/support-tickets/${id}`);
      if (res.data?.ok && res.data.data) {
        setSelected(res.data.data);
      } else {
        alert(res.data?.message || '티켓 상세를 불러오지 못했습니다.');
      }
    } catch (err) {
      alert(errorMessageOf(err, '티켓 상세를 불러오지 못했습니다.'));
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.requesterName.trim() || !form.subject.trim() || !form.message.trim()) {
      alert('요청자, 제목, 내용을 입력해 주세요.');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post<ApiEnvelope<null>>(`${MainURL}/admin-console/support-tickets`, form);
      if (!res.data?.ok) throw new Error(res.data?.message);
      setForm(EMPTY_FORM);
      fetchTickets(statusFilter);
    } catch (err) {
      alert(errorMessageOf(err, '티켓을 등록하지 못했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (id: number, status: TicketStatus) => {
    if (!session) return;
    try {
      const res = await axios.put<ApiEnvelope<null>>(`${MainURL}/admin-console/support-tickets/${id}/status`, {
        requesterId: session.id,
        status,
      });
      if (!res.data?.ok) throw new Error(res.data?.message);
      fetchTickets(statusFilter);
      if (selected?.id === id) setSelected((prev) => (prev ? { ...prev, status } : prev));
    } catch (err) {
      alert(errorMessageOf(err, '상태를 변경하지 못했습니다.'));
    }
  };

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session || !selected) return;
    if (!replyText.trim()) return;
    try {
      const res = await axios.post<ApiEnvelope<null>>(`${MainURL}/admin-console/support-tickets/${selected.id}/replies`, {
        requesterId: session.id,
        message: replyText,
      });
      if (!res.data?.ok) throw new Error(res.data?.message);
      setReplyText('');
      openDetail(selected.id);
      fetchTickets(statusFilter);
    } catch (err) {
      alert(errorMessageOf(err, '답변을 등록하지 못했습니다.'));
    }
  };

  const handleDelete = async (id: number) => {
    if (!session) return;
    if (!window.confirm('이 티켓을 삭제하시겠습니까?')) return;
    try {
      const res = await axios.delete<ApiEnvelope<null>>(`${MainURL}/admin-console/support-tickets/${id}`, {
        params: { requesterId: session.id },
      });
      if (res.data?.ok) {
        setItems((prev) => prev.filter((item) => item.id !== id));
        if (selected?.id === id) setSelected(null);
      } else {
        alert(res.data?.message || '삭제하지 못했습니다.');
      }
    } catch (err) {
      alert(errorMessageOf(err, '삭제하지 못했습니다.'));
    }
  };

  return (
    <div className="Main-cover admin-panel">
      <div className="admin-panel-header">
        <div>
          <h2 className="admin-panel-title">사역자 요청 티켓</h2>
          <p className="admin-panel-desc">교회 사역자의 1:1 요청사항을 티켓으로 접수하고 관리합니다.</p>
        </div>
        <button type="button" className="admin-refresh-btn" onClick={() => fetchTickets(statusFilter)} disabled={loading}>
          {loading ? '불러오는 중...' : '새로고침'}
        </button>
      </div>

      <form className="admin-form" onSubmit={handleCreate}>
        <input
          type="text"
          placeholder="authChurch (선택)"
          value={form.authChurch}
          onChange={(e) => setForm((prev) => ({ ...prev, authChurch: e.target.value }))}
          style={{ maxWidth: 160 }}
        />
        <input
          type="text"
          placeholder="요청자 이름"
          value={form.requesterName}
          onChange={(e) => setForm((prev) => ({ ...prev, requesterName: e.target.value }))}
          style={{ maxWidth: 140 }}
        />
        <input
          type="text"
          placeholder="연락처"
          value={form.requesterContact}
          onChange={(e) => setForm((prev) => ({ ...prev, requesterContact: e.target.value }))}
          style={{ maxWidth: 140 }}
        />
        <input
          type="text"
          placeholder="제목"
          value={form.subject}
          onChange={(e) => setForm((prev) => ({ ...prev, subject: e.target.value }))}
        />
        <textarea
          placeholder="내용"
          value={form.message}
          onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
          rows={2}
        />
        <div className="admin-form-actions">
          <button type="submit" className="admin-refresh-btn" disabled={saving}>
            티켓 등록
          </button>
        </div>
      </form>

      <div className="admin-filterbar">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as '' | TicketStatus)}>
          <option value="">전체 상태</option>
          <option value="open">접수</option>
          <option value="in_progress">처리중</option>
          <option value="resolved">해결됨</option>
          <option value="closed">종료</option>
        </select>
      </div>

      {errorMsg && <p className="admin-error-text">{errorMsg}</p>}

      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>접수일</th>
              <th>교회</th>
              <th>요청자</th>
              <th>제목</th>
              <th>상태</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={6}>등록된 티켓이 없습니다.</td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td>{formatDateTime(item.createdAt)}</td>
                  <td>{item.authChurch || '-'}</td>
                  <td>{item.requesterName}</td>
                  <td style={{ textAlign: 'left', cursor: 'pointer', color: '#007bff' }} onClick={() => openDetail(item.id)}>
                    {item.subject}
                  </td>
                  <td>
                    <select value={item.status} onChange={(e) => handleStatusChange(item.id, e.target.value as TicketStatus)}>
                      {Object.entries(STATUS_LABEL).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button type="button" className="admin-btn view-btn" onClick={() => openDetail(item.id)}>
                      상세
                    </button>
                    <button type="button" className="admin-btn delete-btn" onClick={() => handleDelete(item.id)}>
                      삭제
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="admin-ticket-detail">
          <h3>{selected.subject}</h3>
          <p className="admin-ticket-detail__meta">
            {selected.authChurch || '-'} · {selected.requesterName} ({selected.requesterContact || '연락처 없음'}) ·{' '}
            {formatDateTime(selected.createdAt)}
          </p>
          <p className="admin-ticket-detail__message">{selected.message}</p>

          <div className="admin-ticket-replies">
            {selected.replies.length === 0 ? (
              <p className="admin-ticket-replies__empty">등록된 답변이 없습니다.</p>
            ) : (
              selected.replies.map((reply) => (
                <div key={reply.id} className="admin-ticket-reply">
                  <span className="admin-ticket-reply__author">{reply.authorName || '관리자'}</span>
                  <span className="admin-ticket-reply__date">{formatDateTime(reply.createdAt)}</span>
                  <p>{reply.message}</p>
                </div>
              ))
            )}
          </div>

          <form className="admin-form" onSubmit={handleReply}>
            <textarea
              placeholder="답변 작성"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={2}
            />
            <div className="admin-form-actions">
              <button type="submit" className="admin-refresh-btn">
                답변 등록
              </button>
              <button type="button" className="admin-cancel-btn" onClick={() => setSelected(null)}>
                닫기
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
